import {
  Box,
  Grid,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import {
  ElementType,
  FC,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import useInView from 'react-cool-inview'
import { Fade } from 'utoniq-ui'

import { Loading } from '@/components/organisms/loading'
import { useTranslation } from '@/core'

/**
 *
 * @description useInfinitieScroll
 * 無限スクロールによるページを行うためのフック
 * 戻り値のrefを持つ要素を監視し、画面内に入ったときにonFetchMore関数を実行する。
 *
 * @param onFetchMore observerが画面内に表示された時実行されるフェッチ関数
 * @param hasNextPage 次のページが存在するか、なければobserverの監視を停止する
 * @param inverted スクロールが下→上の方向で行われるかどうか
 * @returns observe 画面内にいるかどうか監視されているref、これをページ要素の一番最後の要素に注入する
 */

const useInfinitieScroll = (
  onFetchMore: () => Promise<void>,
  hasNextPage: boolean,
  inverted?: boolean
) => {
  const { observe } = useInView({
    onEnter: ({ unobserve, observe }) => {
      unobserve()
      if (!hasNextPage) return
      onFetchMore().then(() => {
        observe()
        if (inverted) {
          /**
           * @hack
           * 下→上にスクロールする場合、リスト要素を取り囲む要素にスクロールすることで現在のスクロール位置が崩れることを防ぐ
           */
          const node = document.getElementById('scrollWrapper')
          node?.scrollIntoView()
        }
      })
    },
  })

  return observe
}

type Props = {
  /** 表示順、trueの場合 下→上の順序になる */
  inverted?: boolean
  nodes: JSX.Element[] | undefined
  component?: ElementType
  componentProps?: {}
  loadingComponent?: ReactElement
  itemComponent?: ElementType
  itemComponentProps?: {}
  fadeSx?: {}
  loading: boolean
  hiddenLoading?: boolean
  hasNextPage: boolean
  emptyMessage?: string | JSX.Element
  onFetchMore: () => Promise<void>
}

/**
 * @description
 * 無限スクロールによるページネーションを行うためのコンポーネント
 *
 *
 * @returns
 */
export const ScrollFetcher: FC<Props> = ({
  inverted,
  nodes,
  loading,
  hiddenLoading,
  onFetchMore,
  hasNextPage,
  component: Component = Stack,
  componentProps,
  itemComponent: ItemComponent = Box,
  itemComponentProps,
  emptyMessage,
  loadingComponent = <Box />,
  fadeSx,
}) => {
  const observe = useInfinitieScroll(onFetchMore, hasNextPage, inverted)
  const fetcherAreaRef = useRef<HTMLDivElement>(null)
  // スクロール位置が一番下に達したかどうかの判定
  const [isBottom, setIsBottom] = useState(false)
  const { t } = useTranslation()

  /**
   * 初回レンダリング実行後、全要素の高さが画面以下の場合にページネーションを行う処理
   * スクロール領域の縦の高さを監視し、それが画面よりも小さい場合は再度onFetchMoreを実行する
   */
  useEffect(() => {
    const fetcherArea = fetcherAreaRef.current

    if (!fetcherArea || !hasNextPage || !nodes?.length) return
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentRect.height >= window.innerHeight) {
          return
        }
        onFetchMore()
      }
    })

    observer.observe(fetcherArea)

    return () => {
      observer.disconnect()
    }
  }, [fetcherAreaRef, onFetchMore, nodes?.length, hasNextPage])

  /**
   * スクロール位置が一番下に達したかどうかの判定
   */
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        (document.documentElement && document.documentElement.scrollTop) ||
        document.body.scrollTop
      const scrollHeight =
        (document.documentElement && document.documentElement.scrollHeight) ||
        document.body.scrollHeight
      const clientHeight =
        document.documentElement.clientHeight || window.innerHeight
      const scrolledToBottom =
        Math.ceil(scrollTop + clientHeight) >= scrollHeight

      setIsBottom(scrolledToBottom)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  /**
   * observerが動かない場合でもスクロールページネーションを行うための処理
   * スクロール位置が一番下に達したかどうかを監視し、達した場合はonFetchMoreを実行する
   */
  useEffect(() => {
    if (isBottom && hasNextPage) {
      onFetchMore()
    }
  }, [isBottom, hasNextPage, onFetchMore])

  // show loading
  if (loading && !(nodes && nodes.length)) {
    return loadingComponent
  }

  if (!nodes) return null

  // show empty
  if (!nodes.length) {
    if (typeof emptyMessage !== 'string') {
      return emptyMessage ?? null
    }
    return (
      <Typography
        variant="body1"
        align="center"
        sx={{ py: 6 }}
        color="text.secondary"
      >
        {emptyMessage ?? t('emptyMessage')}
      </Typography>
    )
  }

  return (
    <Box sx={{ pb: 2 }} ref={fetcherAreaRef}>
      <Component
        spacing={4}
        /** invertedが有効な際は、並び順を逆にする。 */
        {...{
          ...(inverted ? { direction: 'column-reverse' } : {}),
          ...componentProps,
        }}
        id="scrollWrapper"
      >
        {nodes.map((node, index) => (
          <ItemComponent
            key={node?.key ?? index}
            /** 下→上スクロールの場合は1番上の要素がobserver */
            /** 上→下スクロールの場合は1番下の要素がobserver */
            ref={index === nodes.length - 1 ? observe : null}
            {...itemComponentProps}
          >
            <Fade sx={{ ...fadeSx }} timeout={225} in>
              {node}
            </Fade>
          </ItemComponent>
        ))}
        {hasNextPage && !hiddenLoading && (
          <Grid
            item
            container
            xs={12}
            sx={{
              m: 'auto',
              justifyContent: 'center',
            }}
          >
            <Loading />
          </Grid>
        )}
      </Component>
    </Box>
  )
}

export const TableScrollFetcher = ({
  onFetchMore,
  loading,
  nodes,
  colSpan,
  hasNextPage,
  loadingComponent = <Box />,
  emptyMessage,
  component: Component = Stack,
}: Props & { colSpan: number }) => {
  const observe = useInfinitieScroll(onFetchMore, hasNextPage)
  const { t } = useTranslation()

  // show loading
  if (loading && !(nodes && nodes.length)) {
    return <TableBody>{loadingComponent}</TableBody>
  }

  if (!nodes) return null

  // show empty
  if (!nodes.length) {
    if (typeof emptyMessage !== 'string') {
      return (
        <TableBody>
          <TableRow>
            <TableCell
              align="center"
              colSpan={colSpan}
              sx={{ borderBottom: 'none' }}
            >
              {emptyMessage ?? null}
            </TableCell>
          </TableRow>
        </TableBody>
      )
    }
    return (
      <TableBody>
        <TableRow>
          <TableCell
            align="center"
            colSpan={colSpan}
            sx={{ borderBottom: 'none' }}
          >
            <Typography
              variant="body1"
              align="center"
              sx={{ py: 6 }}
              color="text.secondary"
            >
              {emptyMessage ?? t('emptyMessage')}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    )
  }

  return (
    <TableBody>
      {nodes.map((node, index) => (
        <Component
          ref={index === nodes.length - 1 ? observe : null}
          key={index}
          sx={{
            '&:hover': {
              bgcolor: 'grey.50',
            },
          }}
        >
          {node}
        </Component>
      ))}

      {loading && (
        <TableRow>
          <TableCell
            align="center"
            colSpan={colSpan}
            sx={{ borderBottom: 'none' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{ m: 'auto' }}
            >
              <Loading />
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}
